import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { DEFAULT_CALENDAR, MARKETS, countryDataSource } from '../../_datasource/CountryDatasource'
import { DomainError } from '../../_kernel/DomainError'
import { ErrorsCode } from '../../_kernel/ErrorsCode'

export type Markets = (typeof ALLOWED_MARKETS)[number]

export const ALLOWED_MARKETS = MARKETS

export const HOSTNAME_TO_MARKET = Object.values(countryDataSource).reduce(
  (acc, config) => {
    if (config.subdomain) {
      acc[`${config.subdomain}.stayforlong.com`] = config.market
    }

    if (config.topdomain) {
      acc[`stayforlong.${config.topdomain}`] = config.market
      acc[`www.stayforlong.${config.topdomain}`] = config.market
    }

    return acc
  },
  {} as { [key: string]: Markets | undefined }
)

export const MarketValidation = z.object({
  value: z.enum(ALLOWED_MARKETS, { required_error: 'value is required' }),
})

export class Market extends Model {
  static create({ value }: z.infer<typeof MarketValidation>) {
    MarketValidation.parse({ value })
    return new Market(value)
  }

  static createOrEmpty({ value }: z.infer<typeof MarketValidation>) {
    try {
      return Market.create({ value })
    } catch {
      return Market.empty()
    }
  }

  static fromHostName({ hostname }: { hostname: string }) {
    const market = HOSTNAME_TO_MARKET[hostname]
    return market ? Market.create({ value: market }) : Market.empty()
  }

  static fromBookingRulesAPI({ market }: { market: string }) {
    return Market.create({ value: market.toLocaleLowerCase() as Markets })
  }

  static default() {
    return Market.create({ value: 'us' })
  }

  static fromJSON(json?: Partial<ReturnType<Market['toJSON']>>) {
    if (!json?.value) {
      return Market.empty()
    }
    return Market.create({ value: json.value })
  }

  static empty() {
    return new Market()
  }

  constructor(private readonly _value?: (typeof ALLOWED_MARKETS)[number]) {
    super()
  }

  get value() { return this._value } // prettier-ignore

  isImperialMetric() {
    return this.value === 'us' || this.value === 'ca'
  }

  daysOfBooking() {
    if (!this._value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    const countryConfig = Object.values(countryDataSource).find(
      ({ market }) => market === this._value
    )
    if (!countryConfig) {
      return DEFAULT_CALENDAR.daysOfBooking
    }
    return countryConfig.calendar.daysOfBooking
  }
  defaultBookingDays() {
    if (!this._value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    const countryConfig = Object.values(countryDataSource).find(
      ({ market }) => market === this._value
    )
    if (!countryConfig) {
      return DEFAULT_CALENDAR.defaultBookingDays
    }
    return countryConfig.calendar.defaultBookingDays
  }
  inanitionDays() {
    if (!this._value) throw DomainError.withCodes(ErrorsCode.MISSING_MARKET)

    const countryConfig = Object.values(countryDataSource).find(
      ({ market }) => market === this._value
    )
    if (!countryConfig) {
      return DEFAULT_CALENDAR.inanitionDays
    }
    return countryConfig.calendar.inanitionDays
  }

  isEmpty(): this is Market & { value: undefined } {
    return !this.value
  }

  isFilled(): this is Market & z.infer<typeof MarketValidation> {
    return !!this.value
  }

  is<T extends Markets>(value: T): this is Market & { value: T } {
    return this.isFilled() && this.value === value
  }

  isOneOf<T extends Markets>(market: readonly T[]): this is Market & { value: T } {
    return this.isFilled() && market.includes(this.value as T)
  }

  toJSON() {
    return { value: this.value ?? null }
  }
}
